'use client';

import React from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface TeamMemberModalProps {
  member: {
    id: number;
    name: string;
    role: string;
    specialization: string;
    image: string;
    alt: string;
    certifications: string[];
    linkedin: string;
    email: string;
    bio: string;
    expertise: string[];
    achievements: string[];
    imagePosition?: string;
  } | null;
  onClose: () => void;
}

const TeamMemberModal = ({ member, onClose }: TeamMemberModalProps) => {
  if (!member) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
      <div className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto glassmorphic rounded-2xl shadow-elevated">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-2 rounded-lg bg-muted hover:bg-primary/20 text-muted-foreground hover:text-primary transition-all duration-300"
          aria-label="Sluit modal"
        >
          <Icon name="XMarkIcon" size={24} />
        </button>

        <div className="p-8">
          <div className="flex flex-col md:flex-row gap-8 mb-8">
            <div className="flex-shrink-0">
              <div className="relative w-48 h-48 rounded-2xl overflow-hidden border-2 border-primary/30">
                <AppImage
                  src={member.image}
                  alt={member.alt}
                  className="w-full h-full object-cover"
                  style={{ objectPosition: member.imagePosition || 'center' }}
                />
              </div>
            </div>

            <div className="flex-1">
              <h2 className="text-3xl font-display font-bold text-foreground mb-2">
                {member.name}
              </h2>
              <p className="text-lg font-semibold text-primary mb-2">{member.role}</p>
              <p className="text-sm text-muted-foreground mb-6">{member.specialization}</p>

              <div className="flex items-center gap-4 mb-6">
                <a
                  href={member.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 rounded-lg bg-primary/10 text-primary hover:bg-primary hover:text-primary-foreground border border-primary/20 hover:border-primary transition-all duration-300 flex items-center gap-2"
                >
                  <Icon name="GlobeAltIcon" size={18} />
                  <span className="text-sm font-semibold">LinkedIn</span>
                </a>
                <a
                  href={`mailto:${member.email}`}
                  className="px-4 py-2 rounded-lg bg-primary/10 text-primary hover:bg-primary hover:text-primary-foreground border border-primary/20 hover:border-primary transition-all duration-300 flex items-center gap-2"
                >
                  <Icon name="EnvelopeIcon" size={18} />
                  <span className="text-sm font-semibold">Email</span>
                </a>
              </div>

              <div className="flex flex-wrap gap-2">
                {member.certifications.map((cert, index) => (
                  <span
                    key={index}
                    className="px-3 py-1.5 text-xs font-medium bg-primary/10 text-primary rounded-full border border-primary/20"
                  >
                    {cert}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-display font-bold text-foreground mb-4 flex items-center gap-2">
                <Icon name="UserIcon" size={24} className="text-primary" />
                <span>Over {member.name.split(' ')[0]}</span>
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{member.bio}</p>
            </div>

            <div>
              <h3 className="text-xl font-display font-bold text-foreground mb-4 flex items-center gap-2">
                <Icon name="SparklesIcon" size={24} className="text-primary" />
                <span>Expertise</span>
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {member.expertise.map((item, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                    <Icon name="CheckCircleIcon" size={20} className="text-success flex-shrink-0" />
                    <span className="text-sm text-foreground">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-xl font-display font-bold text-foreground mb-4 flex items-center gap-2">
                <Icon name="TrophyIcon" size={24} className="text-primary" />
                <span>Prestaties</span>
              </h3>
              <div className="space-y-3">
                {member.achievements.map((achievement, index) => (
                  <div key={index} className="flex items-start gap-3 p-4 rounded-lg bg-muted/50">
                    <Icon name="StarIcon" size={20} className="text-accent flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-foreground">{achievement}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamMemberModal;